import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Calendar, ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart ID to animal mapping (obscured codes)
const CHART_ID_TO_ANIMAL = {
    'x7k2m': 'rat', 'p3n9q': 'ox', 'v8j4w': 'tiger', 'r2f6y': 'rabbit', 
    'h5c1z': 'dragon', 'b9t3x': 'snake', 'n4g8k': 'horse', 'm1d7p': 'goat', 
    'q6s2v': 'monkey', 'w3h9n': 'rooster', 'y8b4j': 'dog', 'c5r1f': 'pig'
};
const ANIMAL_TO_CHART_ID = {
    'rat': 'x7k2m', 'ox': 'p3n9q', 'tiger': 'v8j4w', 'rabbit': 'r2f6y', 
    'dragon': 'h5c1z', 'snake': 'b9t3x', 'horse': 'n4g8k', 'goat': 'm1d7p', 
    'monkey': 'q6s2v', 'rooster': 'w3h9n', 'dog': 'y8b4j', 'pig': 'c5r1f'
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function EnergyBar({ day, peakEnergy, lowEnergy, index, animated }) {
    const [currentHeight, setCurrentHeight] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    
    const getBarColor = () => {
        if (day.energy >= 80) return 'bg-emerald-500';
        if (day.energy < 30) return 'bg-red-500';
        return 'bg-blue-500';
    };
    
    const getTextColor = () => {
        if (day.energy >= 80) return 'text-emerald-600';
        if (day.energy < 30) return 'text-red-600';
        return 'text-blue-600';
    };
    
    const barColor = getBarColor();
    const textColor = getTextColor();
    const targetHeight = Math.max(12, (day.energy / 100) * 220);
    
    useEffect(() => {
        if (animated) {
            const delay = index * 30;
            const timer = setTimeout(() => {
                setCurrentHeight(targetHeight);
            }, delay);
            return () => clearTimeout(timer);
        } else {
            setCurrentHeight(targetHeight);
        }
    }, [animated, index, targetHeight]);
    
    const getCategoryLabel = () => {
        if (day.category === 'BEST') return '上吉';
        if (day.category === 'AVERAGE') return '中';
        return '凶';
    };
    
    return (
        <div 
            className="flex flex-col items-center justify-end flex-1 min-w-0 relative" 
            style={{ height: '260px' }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <span className={`text-[10px] font-semibold mb-1 ${textColor} truncate`}>
                {Math.round(day.energy)}%
            </span>
            <div 
                className={`w-[85%] ${barColor} rounded-t hover:opacity-80 cursor-pointer`}
                style={{ 
                    height: `${currentHeight}px`,
                    transition: 'height 0.5s ease-out'
                }}
            />
            <span className="text-[10px] text-slate-500 mt-1">{day.day}</span>
            
            {showTooltip && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg min-w-[160px]">
                    <div className="font-bold mb-1">Day {day.day} - {getCategoryLabel()}</div>
                    {day.day_officer && <div className="text-slate-300">Officer: {day.day_officer}</div>}
                    {day.constellation && <div className="text-slate-300">宿: {day.constellation}</div>}
                    {day.belt && (
                        <div className={day.is_yellow_path ? "text-yellow-300" : "text-slate-400"}>
                            Belt: {day.is_yellow_path ? "黃道" : "黑道"}
                        </div>
                    )}
                    {day.factors && day.factors.slice(0, 2).map((f, i) => (
                        <div key={i} className="text-slate-400 text-[10px]">{f.factor.split(' ')[0]}: {f.impact}</div>
                    ))}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                </div>
            )}
        </div>
    );
}

function DayListItem({ day, rank, variant }) {
    const getRating = () => {
        if (day.category === 'BEST') return { text: 'BEST', chinese: '上吉', color: 'text-emerald-600' };
        if (day.category === 'AVERAGE') return { text: 'AVERAGE', chinese: '中', color: 'text-blue-600' };
        return { text: 'BAD', chinese: '凶', color: 'text-red-600' };
    };
    
    const rating = getRating();
    const bgColor = variant === 'best' ? 'bg-emerald-50' : 'bg-red-50';
    const badgeColor = variant === 'best' ? 'bg-emerald-500' : 'bg-red-500';
    const valueColor = variant === 'best' ? 'text-emerald-700' : 'text-red-700';
    
    return (
        <div className={`p-3 ${bgColor} rounded-lg`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full ${badgeColor} text-white flex items-center justify-center text-sm font-bold`}>
                        {rank}
                    </span>
                    <div>
                        <span className="font-medium">Day {day.day}</span>
                        {day.day_officer && (
                            <span className="ml-2 text-xs text-slate-500">({day.day_officer})</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {day.is_yellow_path !== undefined && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${day.is_yellow_path ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-200 text-slate-600'}`}>
                            {day.is_yellow_path ? '黃道' : '黑道'}
                        </span>
                    )}
                    <span className={`text-sm font-medium ${rating.color}`}>{rating.chinese}</span>
                    <span className={`${valueColor} font-bold`}>{day.energy}%</span>
                </div>
            </div>
            <div className="mt-2 ml-11 flex flex-wrap gap-1">
                {day.constellation && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/50 rounded text-slate-600">
                        宿: {day.constellation}
                    </span>
                )}
                {day.factors && day.factors.slice(0, 2).map((f, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-white/50 rounded text-slate-600">
                        {f.factor.split(' ')[0]}: {f.impact}
                    </span>
                ))}
            </div>
        </div>
    );
}

function BirthYearInput({ animals, onAnimalFound }) {
    const navigate = useNavigate();
    const [birthYear, setBirthYear] = useState('');
    const [foundAnimal, setFoundAnimal] = useState(null);
    const [error, setError] = useState('');

    const getAnimalFromYear = (year) => {
        const yearNum = parseInt(year);
        if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
            return null;
        }
        
        const animalOrder = ['rat', 'ox', 'tiger', 'rabbit', 'dragon', 'snake', 'horse', 'goat', 'monkey', 'rooster', 'dog', 'pig'];
        const baseYear = 1900;
        const index = (yearNum - baseYear) % 12;
        const animalId = animalOrder[index >= 0 ? index : index + 12];
        
        return animals.find(a => a.id === animalId);
    };

    const handleYearChange = (e) => {
        const value = e.target.value;
        setBirthYear(value);
        setError('');
        
        if (value.length === 4) {
            const animal = getAnimalFromYear(value);
            if (animal) {
                setFoundAnimal(animal);
            } else {
                setFoundAnimal(null);
                setError('Please enter a valid year (1900-2100)');
            }
        } else {
            setFoundAnimal(null);
        }
    };

    const handleViewChart = () => {
        if (foundAnimal) {
            const chartId = ANIMAL_TO_CHART_ID[foundAnimal.id];
            if (chartId) {
                navigate(`/energy-chart/${chartId}`);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="birth-year-input-page">
            <div className="max-w-lg mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">
                        生肖能量圖 <span className="text-slate-400 font-normal text-2xl">Energy Chart</span>
                    </h1>
                    <p className="text-slate-500 text-lg">
                        Enter your birth year to discover your personal chart energy
                    </p>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Birth Year
                        </label>
                        <input
                            type="number"
                            value={birthYear}
                            onChange={handleYearChange}
                            placeholder="e.g., 1990"
                            className="w-full px-4 py-3 text-2xl text-center border-2 border-slate-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                            min="1900"
                            max="2100"
                            data-testid="birth-year-input"
                        />
                        {error && <p className="text-red-500 text-sm mt-2 text-center">{error}</p>}
                    </div>
                    
                    {foundAnimal && (
                        <div className="animate-fade-in">
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 mb-6">
                                <div className="text-center">
                                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-4xl font-bold mb-4 shadow-lg">
                                        {foundAnimal.chinese}
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-1">
                                        Your Zodiac: {foundAnimal.english}
                                    </h2>
                                    <p className="text-slate-500">{foundAnimal.pinyin}</p>
                                    <p className="text-slate-400 text-sm mt-2">
                                        Same sign: {foundAnimal.years.filter(y => y >= parseInt(birthYear) - 24 && y <= parseInt(birthYear) + 24).join(', ')}
                                    </p>
                                </div>
                            </div>
                            
                            <button
                                onClick={handleViewChart}
                                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl"
                                data-testid="view-chart-btn"
                            >
                                View My Energy Chart
                            </button>
                        </div>
                    )}
                    
                    {!foundAnimal && birthYear.length < 4 && (
                        <div className="text-center text-slate-400 py-8">
                            <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-2xl mb-4">
                                🐲
                            </div>
                            <p>Enter your birth year to find your personal chart</p>
                        </div>
                    )}
                </div>
                
                <p className="text-center text-slate-400 text-sm mt-6">
                    For more accuracy, enter your date of birth and time of birth via PRO
                </p>
                
                <div className="flex justify-center mt-4">
                    <button
                        onClick={() => window.location.href = '/subscription'}
                        className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-6 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                    >
                        <span>⭐</span>
                        Upgrade to PRO
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function EnergyChart() {
    const { chartId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user, token } = useAuth();
    
    // Convert chartId to animal name for API calls
    const animal = chartId ? CHART_ID_TO_ANIMAL[chartId] : null;
    
    const [year, setYear] = useState(2026);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [animals, setAnimals] = useState([]);
    const [autoRedirected, setAutoRedirected] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [mode, setMode] = useState('beginner'); // beginner or pro
    const [hasProAccess, setHasProAccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [baziMonthlyEnergy, setBaziMonthlyEnergy] = useState([]);
    const [loadingBazi, setLoadingBazi] = useState(false);
    const [guestName, setGuestName] = useState(null);

    // Check for guest mode from URL query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gName = params.get('guest_name');
        if (gName) setGuestName(decodeURIComponent(gName));
    }, []);

    // Check if user has PRO access and Admin status
    useEffect(() => {
        const checkProAccess = async () => {
            if (!token) {
                setHasProAccess(false);
                setIsAdmin(false);
                return;
            }
            try {
                const response = await fetch(`${API_URL}/api/auth/admin/check`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setHasProAccess(data.has_pro_access);
                    setIsAdmin(data.is_admin || false);
                    // If user has PRO access, set to pro mode
                    if (data.has_pro_access) {
                        setMode('pro');
                    } else {
                        setMode('beginner');
                    }
                }
            } catch (error) {
                console.error('Pro access check error:', error);
                setHasProAccess(false);
                setIsAdmin(false);
            }
        };
        checkProAccess();
    }, [token]);

    const fetchAnimals = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/tong-shu/energy-chart/animals`);
            if (res.ok) {
                const data = await res.json();
                setAnimals(data.animals || []);
            }
        } catch (err) {
            console.error('Failed to fetch animals:', err);
        }
    }, []);

    const fetchChartData = useCallback(async () => {
        if (!animal) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const res = await fetch(`${API_URL}/api/tong-shu/energy-chart/${animal}/${year}/${month}`);
            if (!res.ok) throw new Error('Failed to fetch energy data');
            const data = await res.json();
            setChartData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [animal, year, month]);

    // Fetch monthly energy for PRO users (using real Tong Shu data with streak analysis)
    const fetchBaziMonthlyEnergy = useCallback(async () => {
        if (!hasProAccess || !animal) return;
        
        setLoadingBazi(true);
        try {
            // Fetch all 12 months of real Tong Shu data in parallel
            const promises = Array.from({ length: 12 }, (_, i) => 
                fetch(`${API_URL}/api/tong-shu/energy-chart/${animal}/${year}/${i + 1}`)
                    .then(res => res.ok ? res.json() : null)
            );
            
            const results = await Promise.all(promises);
            
            // Calculate monthly scores with streak analysis
            const monthlyEnergy = results.map((data, i) => {
                if (!data || !data.data || data.data.length === 0) {
                    return { positive: 50, challenge: 25 };
                }
                
                const days = data.data;
                
                // Count green bars (energy >= 80 only - this is the real "green bar" definition)
                const greenDays = days.filter(d => d.energy >= 80);
                const greenCount = greenDays.length;
                
                // Count red bars (energy < 40 or AVOID)
                const redDays = days.filter(d => d.energy < 40 || d.category === 'AVOID');
                const redCount = redDays.length;
                
                // Find green streaks (consecutive green days)
                let greenStreaks = { double: 0, triple: 0, quad: 0, longer: 0 };
                let currentGreenStreak = 0;
                let maxGreenStreak = 0;
                let allGreenStreaks = [];
                
                days.forEach((d, idx) => {
                    if (d.energy >= 70 || d.category === 'BEST' || d.category === 'GOOD') {
                        currentGreenStreak++;
                    } else {
                        if (currentGreenStreak >= 2) allGreenStreaks.push(currentGreenStreak);
                        maxGreenStreak = Math.max(maxGreenStreak, currentGreenStreak);
                        currentGreenStreak = 0;
                    }
                });
                if (currentGreenStreak >= 2) allGreenStreaks.push(currentGreenStreak);
                maxGreenStreak = Math.max(maxGreenStreak, currentGreenStreak);
                
                // Categorize streaks
                allGreenStreaks.forEach(streak => {
                    if (streak >= 5) greenStreaks.longer++;
                    else if (streak >= 4) greenStreaks.quad++;
                    else if (streak >= 3) greenStreaks.triple++;
                    else if (streak >= 2) greenStreaks.double++;
                });
                
                // Find red streaks (consecutive red/avoid days)
                let redStreaks = { double: 0, triple: 0, longer: 0 };
                let currentRedStreak = 0;
                let maxRedStreak = 0;
                
                days.forEach((d, idx) => {
                    if (d.energy < 50 || d.category === 'AVOID') {
                        currentRedStreak++;
                    } else {
                        if (currentRedStreak >= 3) redStreaks.longer++;
                        else if (currentRedStreak >= 2) {
                            if (currentRedStreak === 2) redStreaks.double++;
                            else redStreaks.triple++;
                        }
                        maxRedStreak = Math.max(maxRedStreak, currentRedStreak);
                        currentRedStreak = 0;
                    }
                });
                if (currentRedStreak >= 3) redStreaks.longer++;
                else if (currentRedStreak >= 2) redStreaks.double++;
                maxRedStreak = Math.max(maxRedStreak, currentRedStreak);
                
                // Calculate POSITIVE ENERGY
                let positiveScore = 30; // Base
                
                // Calculate CHALLENGE ENERGY (initialize first)
                let challengeScore = 10; // Base
                
                // Green day count bonus
                positiveScore += greenCount * 2;
                
                // SUPER COMBO: 6+ green bars = BIG BONUS
                // Without Super Combo = PENALTY (both positive goes down, challenge goes up)
                if (greenCount >= 6) {
                    // Super Combo achieved! Big bonus
                    positiveScore += 25 + (greenCount - 6) * 5; // +25 base + 5 per extra green
                } else {
                    // No Super Combo - penalty (the fewer greens, the bigger penalty)
                    positiveScore -= (6 - greenCount) * 12; // -12 per missing green bar
                    // Also increase challenge when no Super Combo
                    challengeScore += (6 - greenCount) * 8; // +8 challenge per missing green
                }
                
                // Average energy of green days bonus
                if (greenDays.length > 0) {
                    const avgGreenEnergy = greenDays.reduce((sum, d) => sum + d.energy, 0) / greenDays.length;
                    positiveScore += (avgGreenEnergy - 70) * 0.2;
                }
                
                // Streak bonuses
                positiveScore += greenStreaks.double * 2;   // +2 per double green
                positiveScore += greenStreaks.triple * 5;   // +5 per triple green
                positiveScore += greenStreaks.quad * 8;     // +8 per quad green
                positiveScore += greenStreaks.longer * 12;  // +12 per 5+ streak
                
                // Longest streak bonus
                positiveScore += maxGreenStreak * 1.5;
                
                // Total winning streaks bonus
                const totalWinningStreaks = allGreenStreaks.length;
                positiveScore += totalWinningStreaks * 2;
                
                // Continue CHALLENGE ENERGY calculation
                // Red day penalty (more red = higher challenge)
                challengeScore += (redCount / days.length) * 25;
                
                // Red streak penalties
                challengeScore += redStreaks.double * 3;    // +3 per double red
                challengeScore += redStreaks.triple * 6;    // +6 per triple red
                challengeScore += redStreaks.longer * 10;   // +10 per 3+ red streak
                
                // Longest red streak penalty
                challengeScore += maxRedStreak * 2;
                
                // Low energy days penalty
                const veryLowDays = days.filter(d => d.energy < 30).length;
                challengeScore += veryLowDays * 1.5;
                
                return {
                    positive: Math.min(85, Math.max(30, Math.round(positiveScore))),
                    challenge: Math.min(50, Math.max(10, Math.round(challengeScore)))
                };
            });
            
            setBaziMonthlyEnergy(monthlyEnergy);
        } catch (err) {
            console.error('Failed to fetch monthly energy:', err);
        } finally {
            setLoadingBazi(false);
        }
    }, [hasProAccess, animal, year]);

    useEffect(() => {
        fetchAnimals();
    }, [fetchAnimals]);

    useEffect(() => {
        fetchChartData();
    }, [fetchChartData]);

    // Fetch monthly energy when PRO access is confirmed
    useEffect(() => {
        if (hasProAccess && animal) {
            fetchBaziMonthlyEnergy();
        }
    }, [hasProAccess, animal, year, fetchBaziMonthlyEnergy]);

    // Auto-redirect logged-in users to their personal chart
    useEffect(() => {
        if (!chartId && !autoRedirected && isAuthenticated && user?.birth_animal) {
            const userAnimal = user.birth_animal.toLowerCase();
            const userChartId = ANIMAL_TO_CHART_ID[userAnimal];
            if (userChartId) {
                setAutoRedirected(true);
                navigate(`/energy-chart/${userChartId}`, { replace: true });
            }
        }
    }, [chartId, autoRedirected, isAuthenticated, user, navigate]);

    const handleAnimalChange = (e) => {
        const selectedAnimal = e.target.value;
        const selectedChartId = ANIMAL_TO_CHART_ID[selectedAnimal];
        if (selectedChartId) {
            navigate(`/energy-chart/${selectedChartId}`);
        }
    };

    const handleExportPDF = () => {
        if (!chartData) return;
        setExporting(true);
        
        try {
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                setExporting(false);
                return;
            }

            const animalInfo = chartData.animal;
            const stats = chartData.statistics;
            const data = chartData.data;
            
            // Calculate all forecast data (same logic as ChartSection)
            const greenBars = data.filter(d => d.energy >= 80).length;
            const redBars = data.filter(d => d.energy < 30).length;
            const blueBars = data.filter(d => d.energy >= 30 && d.energy < 80).length;
            const avgEnergy = data.reduce((sum, d) => sum + d.energy, 0) / data.length;
            
            // Count all factors
            const yellowPathDays = data.filter(d => d.is_yellow_path).length;
            const blackPathDays = data.filter(d => !d.is_yellow_path).length;
            const bestRatingDays = data.filter(d => d.category === 'BEST').length;
            const badRatingDays = data.filter(d => d.category === 'BAD').length;
            const averageRatingDays = data.filter(d => d.category === 'AVERAGE').length;
            const dayBreakerDays = data.filter(d => d.is_day_breaker).length;
            
            // Count Day Officers
            const goodOfficers = ['Success', 'Danger', 'Open', 'Full'];
            const badOfficers = ['Destruction', 'Close'];
            const goodOfficerDays = data.filter(d => goodOfficers.includes(d.day_officer)).length;
            const badOfficerDays = data.filter(d => badOfficers.includes(d.day_officer)).length;
            
            // Count Constellations
            const goodConstellations = ['Room', 'Heart', 'Tail', 'Chamber', 'Wall', 'Stomach', 'Pleiades', 'Well', 'Ghost', 'Willow', 'Star', 'Wings'];
            const badConstellations = ['Ox', 'Girl', 'Void', 'Rooftop', 'Net', 'Beak', 'Chariot'];
            const goodConstellationDays = data.filter(d => goodConstellations.includes(d.constellation)).length;
            const badConstellationDays = data.filter(d => badConstellations.includes(d.constellation)).length;
            
            // Count stars
            let totalAuspiciousStars = 0;
            let totalInauspiciousStars = 0;
            data.forEach(d => {
                if (d.auspicious_stars) totalAuspiciousStars += d.auspicious_stars.length;
                if (d.inauspicious_stars) totalInauspiciousStars += d.inauspicious_stars.length;
            });
            
            // Calculate winning streaks
            const getWinningStreaks = () => {
                const streaks = [];
                let currentStreak = null;
                
                data.forEach((day) => {
                    const isWinning = day.energy >= 30;
                    
                    if (isWinning) {
                        if (!currentStreak) {
                            currentStreak = { start: day.day, end: day.day, days: [day], energies: [day.energy], qualities: [day.total_quality || 0] };
                        } else {
                            currentStreak.end = day.day;
                            currentStreak.days.push(day);
                            currentStreak.energies.push(day.energy);
                            currentStreak.qualities.push(day.total_quality || 0);
                        }
                    } else {
                        if (currentStreak && currentStreak.days.length >= 2) {
                            currentStreak.avgEnergy = currentStreak.energies.reduce((a, b) => a + b, 0) / currentStreak.energies.length;
                            currentStreak.totalQuality = currentStreak.qualities.reduce((a, b) => a + b, 0);
                            currentStreak.bestDays = currentStreak.days.filter(d => d.category === 'BEST').length;
                            currentStreak.yellowPathCount = currentStreak.days.filter(d => d.is_yellow_path).length;
                            currentStreak.goodOfficerCount = currentStreak.days.filter(d => goodOfficers.includes(d.day_officer)).length;
                            streaks.push(currentStreak);
                        }
                        currentStreak = null;
                    }
                });
                
                if (currentStreak && currentStreak.days.length >= 2) {
                    currentStreak.avgEnergy = currentStreak.energies.reduce((a, b) => a + b, 0) / currentStreak.energies.length;
                    currentStreak.totalQuality = currentStreak.qualities.reduce((a, b) => a + b, 0);
                    currentStreak.bestDays = currentStreak.days.filter(d => d.category === 'BEST').length;
                    currentStreak.yellowPathCount = currentStreak.days.filter(d => d.is_yellow_path).length;
                    currentStreak.goodOfficerCount = currentStreak.days.filter(d => goodOfficers.includes(d.day_officer)).length;
                    streaks.push(currentStreak);
                }
                
                return streaks;
            };
            
            const streaks = getWinningStreaks();
            const bestStreak = streaks.length > 0 ? streaks.reduce((best, s) => s.totalQuality > best.totalQuality ? s : best, streaks[0]) : null;
            
            // Determine classification
            let classification = 'Promising';
            let icon = '✨';
            let classColor = '#3b82f6';
            
            if (greenBars >= 6 && redBars <= 3) {
                classification = 'Super Combo';
                icon = '🌟';
                classColor = '#10b981';
            } else if (redBars >= 8) {
                classification = 'Challenging';
                icon = '🔴';
                classColor = '#ef4444';
            } else if (greenBars >= 4 && redBars <= 5) {
                classification = 'Promising';
                icon = '✨';
                classColor = '#3b82f6';
            } else if (redBars >= 6) {
                classification = 'Promising (Caution)';
                icon = '⚠️';
                classColor = '#f59e0b';
            }
            
            // Generate forecast text
            const positiveScore = yellowPathDays + bestRatingDays + goodOfficerDays + goodConstellationDays + (totalAuspiciousStars / 3);
            const negativeScore = blackPathDays + badRatingDays + badOfficerDays + dayBreakerDays + badConstellationDays + (totalInauspiciousStars / 3);
            
            let expectation = '';
            let decisionAdvice = '';
            let energyDescription = '';
            
            if (greenBars >= 6) {
                expectation = `This month radiates exceptional cosmic energy for your personal chart. With ${greenBars} peak energy days reaching 80% or higher, the celestial alignments strongly favor bold initiatives, important life decisions, and new ventures. The presence of ${yellowPathDays} Yellow Path (黃道) days indicates divine timing support, while ${goodOfficerDays} days blessed by favorable Day Officers (Success, Danger, Open, Full) amplify your chances of achievement. The ${goodConstellationDays} days with auspicious 28 Constellations (二十八宿) create windows of heightened opportunity. This is truly a Super Combo month where the stars align in your favor.`;
                decisionAdvice = `Maximize this exceptional period by scheduling major decisions, contract signings, business launches, and important negotiations during your green-bar days. The ${bestRatingDays} days with BEST personal chart compatibility are ideal for personal matters and relationships. With ${totalAuspiciousStars} auspicious star (吉星) influences throughout the month, your endeavors receive celestial blessings. However, remain mindful of the ${dayBreakerDays} Day Breaker (日破) days when your animal sign faces direct clash – avoid major commitments on these dates. Use the ${blackPathDays} Black Path (黑道) days for rest, planning, and low-key activities rather than important launches.`;
                energyDescription = `Your monthly energy profile shows a powerful distribution: ${greenBars} high-energy days, ${blueBars} stable days, and only ${redBars} challenging days. The ${totalAuspiciousStars} auspicious stars versus ${totalInauspiciousStars} inauspicious stars ratio favors positive outcomes. Among the 12 Day Officers (建除十二神), you benefit from ${goodOfficerDays} favorable placements. The 28 Lunar Mansions contribute ${goodConstellationDays} auspicious alignments. This comprehensive cosmic support makes this an outstanding month for progress and achievement.`;
            } else if (redBars >= 8) {
                expectation = `This month requires heightened awareness and strategic navigation. With ${redBars} low-energy days falling below 30%, combined with ${dayBreakerDays} Day Breaker (日破) clashes against your personal chart, the cosmic currents present notable challenges. The presence of ${badOfficerDays} unfavorable Day Officers (Destruction, Close) and ${badConstellationDays} inauspicious constellation days demands careful timing. The ${totalInauspiciousStars} inauspicious star (凶星) influences suggest exercising patience and avoiding impulsive decisions. However, with ${greenBars} green-bar days still available, strategic windows for action exist.`;
                decisionAdvice = `Navigate this period by concentrating important activities on your ${greenBars} peak energy days and ${bestRatingDays} BEST-rated days. Absolutely avoid major decisions, investments, or confrontations during Day Breaker (日破) periods when your sign faces direct cosmic clash. The ${blackPathDays} Black Path (黑道) days are best reserved for introspection, planning, and completing existing projects rather than initiating new ones. Leverage the ${yellowPathDays} Yellow Path (黃道) days for time-sensitive matters. Pay attention to the ${goodOfficerDays} favorable Day Officer placements as safe havens for moderate activity.`;
                energyDescription = `Analysis reveals ${redBars} challenging days requiring caution, balanced against ${greenBars} high-opportunity days and ${blueBars} neutral days. The ${totalInauspiciousStars} inauspicious stars (凶星) outweigh the ${totalAuspiciousStars} auspicious influences, indicating a need for defensive positioning. Among the 28 Constellations, ${badConstellationDays} days carry negative mansion influences. Focus on damage control during difficult periods and capitalize fully on the available positive windows.`;
            } else {
                expectation = `This month offers promising energy potential with solid foundations for progress. Your ${greenBars} high-energy days provide excellent timing for important matters, supported by ${yellowPathDays} Yellow Path (黃道) auspicious days. The ${bestRatingDays} days with BEST personal chart compatibility enhance personal endeavors, while ${goodOfficerDays} favorable Day Officer placements (Success, Danger, Open, Full) create productive windows. With ${goodConstellationDays} favorable constellation days from the 28 Lunar Mansions (二十八宿), the month supports steady advancement.`;
                decisionAdvice = `Build momentum by aligning important activities with your strongest energy days. Prioritize the ${greenBars} peak days for major decisions, contracts, and launches. The ${bestRatingDays} BEST compatibility days favor personal relationships and individual goals. Utilize the ${yellowPathDays} Yellow Path (黃道) days for matters requiring luck and divine timing. Maintain awareness of the ${dayBreakerDays} Day Breaker (日破) days when your sign faces clash – schedule rest or routine tasks during these periods. The ${totalAuspiciousStars} auspicious star blessings throughout the month support positive outcomes when properly timed.`;
                energyDescription = `Your energy profile reveals a constructive distribution: ${greenBars} excellent days, ${blueBars} stable days, and ${redBars} challenging days requiring caution. The 12 Day Officers (建除十二神) provide ${goodOfficerDays} favorable placements against ${badOfficerDays} unfavorable ones. Among the 28 Constellations, ${goodConstellationDays} days carry beneficial mansion energy. The stellar balance shows ${totalAuspiciousStars} auspicious influences (吉星) supporting your endeavors. This Promising month rewards consistent effort and wise timing.`;
            }
            
            // Generate bar chart HTML with streak indicators
            const barsHTML = data.map((day, index) => {
                const height = Math.max(10, (day.energy / 100) * 150);
                const color = day.energy >= 80 ? '#10b981' : day.energy < 30 ? '#ef4444' : '#3b82f6';
                return `<div style="display:flex;flex-direction:column;align-items:center;flex:1;min-width:0;">
                    <span style="font-size:8px;color:${color};font-weight:600;">${Math.round(day.energy)}%</span>
                    <div style="width:80%;height:${height}px;background:${color};border-radius:3px 3px 0 0;"></div>
                    <span style="font-size:8px;color:#64748b;margin-top:2px;">${day.day}</span>
                </div>`;
            }).join('');

            // Generate detailed best days
            const bestDaysHTML = stats.best_days?.slice(0, 5).map(d => {
                const dayData = data.find(dd => dd.day === d.day) || {};
                const rating = dayData.category === 'BEST' ? '上吉' : dayData.category === 'AVERAGE' ? '中' : '凶';
                return `<div style="background:#f0fdf4;border-radius:12px;padding:12px;margin-bottom:8px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="background:#10b981;color:white;padding:4px 10px;border-radius:6px;font-weight:700;font-size:14px;">Day ${d.day}</span>
                            <span style="color:#166534;font-size:12px;">${dayData.day_officer || ''}</span>
                        </div>
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="background:#dcfce7;color:#166534;padding:2px 8px;border-radius:4px;font-size:11px;">${rating}</span>
                            <span style="font-weight:700;color:#10b981;font-size:16px;">${d.energy}%</span>
                        </div>
                    </div>
                    <div style="font-size:11px;color:#64748b;">
                        ${dayData.constellation ? `宿: ${dayData.constellation}` : ''} 
                        ${dayData.is_yellow_path ? '• 黃道' : '• 黑道'}
                        ${dayData.factors?.slice(0, 2).map(f => `• ${f.factor.split(' ')[0]}: ${f.impact > 0 ? '+' : ''}${f.impact}`).join(' ') || ''}
                    </div>
                </div>`;
            }).join('') || '';

            // Generate detailed worst days
            const worstDaysHTML = stats.worst_days?.slice(0, 5).map(d => {
                const dayData = data.find(dd => dd.day === d.day) || {};
                const rating = dayData.category === 'BEST' ? '上吉' : dayData.category === 'AVERAGE' ? '中' : '凶';
                return `<div style="background:#fef2f2;border-radius:12px;padding:12px;margin-bottom:8px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="background:#ef4444;color:white;padding:4px 10px;border-radius:6px;font-weight:700;font-size:14px;">Day ${d.day}</span>
                            <span style="color:#991b1b;font-size:12px;">${dayData.day_officer || ''}</span>
                        </div>
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="background:#fee2e2;color:#991b1b;padding:2px 8px;border-radius:4px;font-size:11px;">${rating}</span>
                            <span style="font-weight:700;color:#ef4444;font-size:16px;">${d.energy}%</span>
                        </div>
                    </div>
                    <div style="font-size:11px;color:#64748b;">
                        ${dayData.constellation ? `宿: ${dayData.constellation}` : ''} 
                        ${dayData.is_yellow_path ? '• 黃道' : '• 黑道'}
                        ${dayData.is_day_breaker ? '• 日破' : ''}
                    </div>
                </div>`;
            }).join('') || '';

            // Generate caution zones (consecutive red bars)
            const getCautionZones = () => {
                const zones = [];
                let currentZone = null;
                
                data.forEach((day) => {
                    const isRed = day.energy < 30;
                    
                    if (isRed) {
                        if (!currentZone) {
                            currentZone = { start: day.day, end: day.day, days: [day] };
                        } else {
                            currentZone.end = day.day;
                            currentZone.days.push(day);
                        }
                    } else {
                        if (currentZone && currentZone.days.length >= 2) {
                            zones.push(currentZone);
                        }
                        currentZone = null;
                    }
                });
                
                if (currentZone && currentZone.days.length >= 2) {
                    zones.push(currentZone);
                }
                
                return zones;
            };
            
            const cautionZones = getCautionZones();

            // Generate best winning streak banner with top 3 stars ranking
            const rankedStreaks = [...streaks].sort((a, b) => b.totalQuality - a.totalQuality).slice(0, 3);
            const getStars = (rank) => rank === 0 ? '⭐⭐⭐' : rank === 1 ? '⭐⭐' : '⭐';
            const getRankLabel = (rank) => rank === 0 ? 'Best' : rank === 1 ? '2nd Best' : '3rd Best';
            const getRankBg = (rank) => rank === 0 ? 'linear-gradient(135deg,#fef3c7,#fde68a)' : rank === 1 ? 'linear-gradient(135deg,#fef9c3,#fef08a)' : 'linear-gradient(135deg,#fffbeb,#fef3c7)';
            const getRankBorder = (rank) => rank === 0 ? '#f59e0b' : rank === 1 ? '#fbbf24' : '#fcd34d';
            
            const topStreaksBannerHTML = rankedStreaks.map((s, rank) => `
                <div style="background:${getRankBg(rank)};border:2px solid ${getRankBorder(rank)};border-radius:12px;padding:14px;margin-bottom:10px;">
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
                        <span style="font-size:16px;">${getStars(rank)}</span>
                        <span style="font-weight:700;color:#92400e;font-size:14px;">${getRankLabel(rank)} Streak: Days ${s.start}-${s.end} (${s.days.length} days, avg ${s.avgEnergy.toFixed(0)}%)</span>
                    </div>
                    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-left:28px;">
                        <span style="background:#dcfce7;color:#166534;padding:3px 8px;border-radius:5px;font-size:11px;">${s.bestDays} BEST days</span>
                        <span style="background:#fef9c3;color:#854d0e;padding:3px 8px;border-radius:5px;font-size:11px;">${s.yellowPathCount} 黃道 days</span>
                        <span style="background:#dbeafe;color:#1e40af;padding:3px 8px;border-radius:5px;font-size:11px;">${s.goodOfficerCount} good officers</span>
                        <span style="background:#f3e8ff;color:#7c3aed;padding:3px 8px;border-radius:5px;font-size:11px;">Quality: ${s.totalQuality}</span>
                    </div>
                </div>
            `).join('');

            // Generate chart with winning streak boxes
            const barWidth = 100 / data.length;
            const chartBarsHTML = data.map((day, index) => {
                const height = Math.max(10, (day.energy / 100) * 140);
                const color = day.energy >= 80 ? '#10b981' : day.energy < 30 ? '#ef4444' : '#3b82f6';
                return `<div style="display:flex;flex-direction:column;align-items:center;flex:1;min-width:0;position:relative;z-index:10;">
                    <span style="font-size:7px;color:${color};font-weight:600;">${Math.round(day.energy)}%</span>
                    <div style="width:80%;height:${height}px;background:${color};border-radius:2px 2px 0 0;"></div>
                    <span style="font-size:7px;color:#64748b;margin-top:2px;">${day.day}</span>
                </div>`;
            }).join('');

            // Generate streak overlay boxes with star ranking
            const streakOverlaysHTML = streaks.map((s, idx) => {
                // Find rank in sorted array
                const rank = rankedStreaks.findIndex(rs => rs.start === s.start && rs.end === s.end);
                const isRanked = rank !== -1;
                const stars = rank === 0 ? '⭐⭐⭐' : rank === 1 ? '⭐⭐' : rank === 2 ? '⭐' : '';
                const borderColor = isRanked ? '#f59e0b' : '#14b8a6';
                const bgColor = isRanked ? `rgba(254,243,199,${0.4 - rank * 0.1})` : 'rgba(204,251,241,0.3)';
                const leftPercent = ((s.start - 1) / data.length) * 100;
                const widthPercent = (s.days.length / data.length) * 100;
                
                // Check for hot streaks (consecutive greens)
                let hotStreakLabel = '';
                const maxConsecutiveGreens = s.days.reduce((max, d, i, arr) => {
                    let count = 0;
                    for (let j = i; j < arr.length && arr[j].energy >= 80; j++) count++;
                    return Math.max(max, count);
                }, 0);
                
                if (maxConsecutiveGreens >= 3) hotStreakLabel = '★★★ HOT';
                else if (maxConsecutiveGreens >= 2) hotStreakLabel = '★★ DOUBLE';
                
                return `<div style="position:absolute;left:${leftPercent}%;width:${widthPercent}%;top:0;bottom:20px;border:2px dashed ${borderColor};border-radius:8px;background:${bgColor};pointer-events:none;">
                    <div style="position:absolute;top:-24px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;">
                        ${stars ? `<span style="font-size:10px;margin-bottom:1px;">${stars}</span>` : ''}
                        <span style="background:${borderColor};color:white;padding:2px 6px;border-radius:4px;font-size:9px;font-weight:700;white-space:nowrap;">
                            ${s.days.length} DAYS
                        </span>
                    </div>
                    ${hotStreakLabel ? `<div style="position:absolute;top:5px;left:50%;transform:translateX(-50%);color:${borderColor};font-size:8px;font-weight:700;">${hotStreakLabel}</div>` : ''}
                    <div style="position:absolute;bottom:-16px;left:50%;transform:translateX(-50%);font-size:8px;color:#64748b;">Q:${s.totalQuality}</div>
                </div>`;
            }).join('');

            // Generate caution zone overlays
            const cautionOverlaysHTML = cautionZones.map(zone => {
                const leftPercent = ((zone.start - 1) / data.length) * 100;
                const widthPercent = (zone.days.length / data.length) * 100;
                const label = zone.days.length >= 3 ? '⚠ DANGER' : '⚠ CAUTION';
                
                return `<div style="position:absolute;left:${leftPercent}%;width:${widthPercent}%;top:0;bottom:20px;border:2px dashed #ef4444;border-radius:8px;background:rgba(254,226,226,0.3);pointer-events:none;">
                    <div style="position:absolute;top:-18px;left:50%;transform:translateX(-50%);color:#ef4444;font-size:9px;font-weight:700;white-space:nowrap;">${label}</div>
                </div>`;
            }).join('');

            const html = `<!DOCTYPE html>
            <html>
            <head>
                <title>PRO Energy Report - ${animalInfo.english} - ${MONTH_NAMES[month - 1]} ${year}</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px; background: #f8fafc; color: #1e293b; font-size: 13px; line-height: 1.5; }
                    .container { max-width: 850px; margin: 0 auto; background: white; border-radius: 16px; padding: 28px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
                    .header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e2e8f0; }
                    .pro-badge { display: inline-block; background: linear-gradient(135deg, #f59e0b, #ea580c); color: white; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; }
                    .header h1 { font-size: 22px; color: #0f172a; margin-bottom: 4px; font-weight: 700; }
                    .subtitle { font-size: 12px; color: #64748b; }
                    
                    .section { margin-bottom: 20px; page-break-inside: avoid; }
                    .section-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
                    
                    .forecast-box { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #bbf7d0; border-radius: 14px; padding: 20px; }
                    .forecast-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #bbf7d0; }
                    .forecast-class { font-size: 20px; font-weight: 700; color: ${classColor}; }
                    .forecast-avg-value { font-size: 28px; font-weight: 700; color: #0f172a; }
                    .forecast-avg-label { font-size: 11px; color: #64748b; }
                    
                    .energy-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
                    .energy-stat { text-align: center; padding: 10px; border-radius: 10px; }
                    .energy-stat-value { font-size: 22px; font-weight: 700; }
                    .energy-stat-label { font-size: 10px; margin-top: 2px; }
                    
                    .factors-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #bbf7d0; }
                    .factor-item { text-align: center; padding: 8px; background: white; border-radius: 8px; }
                    .factor-value { font-size: 16px; font-weight: 700; }
                    .factor-label { font-size: 9px; color: #64748b; margin-top: 2px; }
                    
                    .forecast-text { margin-top: 20px; padding-top: 20px; border-top: 2px solid #bbf7d0; }
                    .forecast-section { 
                        background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%); 
                        border-radius: 12px; 
                        padding: 16px 18px; 
                        margin-bottom: 14px; 
                        border-left: 5px solid #10b981; 
                        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                        page-break-inside: avoid;
                    }
                    .forecast-section h4 { 
                        font-size: 14px; 
                        font-weight: 700; 
                        color: #166534; 
                        margin-bottom: 10px; 
                        padding-bottom: 8px;
                        border-bottom: 1px dashed #d1fae5;
                    }
                    .forecast-section p { 
                        font-size: 11px; 
                        color: #374151; 
                        line-height: 1.8; 
                        text-align: justify; 
                        margin: 0;
                    }
                    .forecast-section.blue { border-left-color: #3b82f6; }
                    .forecast-section.blue h4 { color: #1e40af; border-bottom-color: #dbeafe; }
                    .forecast-section.amber { border-left-color: #f59e0b; }
                    .forecast-section.amber h4 { color: #b45309; border-bottom-color: #fef3c7; }
                    
                    .page-break { page-break-before: always; }
                    
                    .chart-wrapper { position: relative; padding-top: 30px; padding-bottom: 25px; }
                    .chart-bars { display: flex; align-items: flex-end; gap: 1px; height: 160px; }
                    
                    .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
                    .stat-card { background: #f8fafc; border-radius: 10px; padding: 14px; text-align: center; }
                    .stat-value { font-size: 24px; font-weight: 700; color: #0f172a; }
                    .stat-label { font-size: 11px; color: #64748b; margin-top: 2px; }
                    
                    .days-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                    .days-column h3 { font-size: 13px; font-weight: 600; margin-bottom: 10px; }
                    
                    .chart-legend { display: flex; flex-wrap: wrap; justify-content: center; gap: 16px; padding: 12px; background: #f8fafc; border-radius: 10px; margin-top: 16px; font-size: 10px; color: #64748b; }
                    .legend-item { display: flex; align-items: center; gap: 4px; }
                    .legend-color { width: 12px; height: 8px; border-radius: 2px; }
                    
                    .print-btn { display: block; margin: 20px auto 0; padding: 10px 20px; background: #f59e0b; color: white; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
                    @media print { .print-btn { display: none; } }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="header">
                        <div class="pro-badge">✦ PRO ENERGY REPORT ✦</div>
                        <h1>Monthly Energy Analysis</h1>
                        <p class="subtitle">${MONTH_NAMES[month - 1]} ${year}</p>
                    </div>
                    
                    <!-- Monthly Energy Forecast -->
                    <div class="section">
                        <div class="section-title">${icon} Monthly Energy Forecast</div>
                        <div class="forecast-box">
                            <div class="forecast-header">
                                <div class="forecast-class">${classification}</div>
                                <div style="text-align:right;">
                                    <div class="forecast-avg-value">${avgEnergy.toFixed(0)}%</div>
                                    <div class="forecast-avg-label">Average Energy</div>
                                </div>
                            </div>
                            
                            <div class="energy-grid">
                                <div class="energy-stat" style="background:#dcfce7;">
                                    <div class="energy-stat-value" style="color:#16a34a;">${greenBars}</div>
                                    <div class="energy-stat-label" style="color:#166534;">Green Days (80%+)</div>
                                </div>
                                <div class="energy-stat" style="background:#dbeafe;">
                                    <div class="energy-stat-value" style="color:#2563eb;">${blueBars}</div>
                                    <div class="energy-stat-label" style="color:#1e40af;">Blue Days (30-79%)</div>
                                </div>
                                <div class="energy-stat" style="background:#fee2e2;">
                                    <div class="energy-stat-value" style="color:#dc2626;">${redBars}</div>
                                    <div class="energy-stat-label" style="color:#991b1b;">Red Days (&lt;30%)</div>
                                </div>
                            </div>
                            
                            <div class="factors-grid">
                                <div class="factor-item"><div class="factor-value" style="color:#eab308;">${yellowPathDays}</div><div class="factor-label">Yellow Path 黃道</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#64748b;">${blackPathDays}</div><div class="factor-label">Black Path 黑道</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#10b981;">${bestRatingDays}</div><div class="factor-label">BEST Rating</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#10b981;">${goodOfficerDays}</div><div class="factor-label">Good Officers</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#ef4444;">${dayBreakerDays}</div><div class="factor-label">Day Breaker 日破</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#10b981;">${goodConstellationDays}</div><div class="factor-label">Good 宿</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#10b981;">${totalAuspiciousStars}</div><div class="factor-label">吉星</div></div>
                                <div class="factor-item"><div class="factor-value" style="color:#ef4444;">${totalInauspiciousStars}</div><div class="factor-label">凶星</div></div>
                            </div>
                            
                            <div class="forecast-text">
                                <div class="forecast-section">
                                    <h4>📊 MONTHLY EXPECTATION</h4>
                                    <p>${expectation}</p>
                                </div>
                                <div class="forecast-section blue">
                                    <h4>🎯 DECISION MAKING GUIDE</h4>
                                    <p>${decisionAdvice}</p>
                                </div>
                                <div class="forecast-section amber">
                                    <h4>⚡ ENERGY ANALYSIS</h4>
                                    <p>${energyDescription}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Top Winning Streaks Banner with Star Ranking -->
                    <div class="page-break"></div>
                    <div class="section">
                        <div class="section-title">🏆 Top Winning Streaks</div>
                        ${topStreaksBannerHTML}
                    </div>
                    
                    <!-- Energy Chart with Overlays -->
                    <div class="section">
                        <div class="section-title">📈 Daily Energy Chart</div>
                        <div style="background:#f8fafc;border-radius:12px;padding:16px;">
                            <div class="chart-wrapper">
                                ${streakOverlaysHTML}
                                ${cautionOverlaysHTML}
                                <div class="chart-bars">${chartBarsHTML}</div>
                            </div>
                            <div style="text-align:center;color:#64748b;font-size:10px;">Day of Month</div>
                        </div>
                        
                        <div class="chart-legend">
                            <span style="font-weight:600;">Winning Streaks:</span> Consecutive days with energy ≥30%
                            <span class="legend-item"><span style="color:#f59e0b;">★★★</span> Hot Streak</span>
                            <span class="legend-item"><span style="color:#14b8a6;">★★</span> Double Green</span>
                            <span class="legend-item"><span style="color:#ef4444;">⚠</span> Caution</span>
                            <span class="legend-item"><span style="color:#ef4444;font-weight:700;">⚠</span> Danger Zone</span>
                            <span class="legend-item"><span style="color:#f59e0b;">★</span> Best Streak</span>
                        </div>
                    </div>
                    
                    <!-- Stats -->
                    <div class="stats-grid">
                        <div class="stat-card">
                            <div class="stat-value">${stats.average}%</div>
                            <div class="stat-label">Average Energy</div>
                        </div>
                        <div class="stat-card" style="background:#f0fdf4;">
                            <div class="stat-value" style="color:#10b981;">Day ${stats.peak_day?.day}</div>
                            <div class="stat-label">Peak Day (${stats.peak_day?.energy}%)</div>
                        </div>
                        <div class="stat-card" style="background:#fef2f2;">
                            <div class="stat-value" style="color:#ef4444;">Day ${stats.low_day?.day}</div>
                            <div class="stat-label">Lowest Day (${stats.low_day?.energy}%)</div>
                        </div>
                    </div>
                    
                    <!-- Best & Worst Days -->
                    <div class="section">
                        <div class="days-grid">
                            <div class="days-column">
                                <h3 style="color:#166534;">🌟 Best Days This Month</h3>
                                ${bestDaysHTML}
                            </div>
                            <div class="days-column">
                                <h3 style="color:#991b1b;">⚠️ Days to Be Cautious</h3>
                                ${worstDaysHTML}
                            </div>
                        </div>
                    </div>
                    
                    <!-- Color Legend -->
                    <div style="display:flex;justify-content:center;gap:20px;padding:14px;background:#f8fafc;border-radius:10px;">
                        <span class="legend-item"><span class="legend-color" style="background:#10b981;"></span> High Energy (80%+)</span>
                        <span class="legend-item"><span class="legend-color" style="background:#3b82f6;"></span> Average (30-79%)</span>
                        <span class="legend-item"><span class="legend-color" style="background:#ef4444;"></span> Low Energy (&lt;30%)</span>
                    </div>
                    
                    <button onclick="window.print()" class="print-btn">🖨️ Print / Save as PDF</button>
                </div>
            </body>
            </html>`;

            printWindow.document.write(html);
            printWindow.document.close();
        } catch (err) {
            console.error('Export failed:', err);
        }
        setExporting(false);
    };

    // Show birth year input only if:
    // 1. No chartId param in URL
    // 2. AND (not logged in OR logged in but no birth_animal)
    if (!chartId) {
        // If logged in with birth_animal, the useEffect above will redirect
        // Show loading while waiting for redirect
        if (isAuthenticated && user?.birth_animal) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-slate-500">Loading your personal chart...</p>
                    </div>
                </div>
            );
        }
        return <BirthYearInput animals={animals} />;
    }

    const peakEnergy = chartData?.statistics?.peak_day?.energy;
    const lowEnergy = chartData?.statistics?.low_day?.energy;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="energy-chart-page">
            <div className="max-w-6xl mx-auto">
                {/* Guest Banner */}
                {guestName && (
                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center justify-between" data-testid="guest-banner">
                        <div className="flex items-center gap-3">
                            <span className="font-chinese text-xl">{chartData?.animal_chinese || ''}</span>
                            <div>
                                <span className="font-bold text-indigo-800">{guestName}</span>
                                <span className="text-sm text-indigo-600 ml-2">Guest · {animal}</span>
                            </div>
                        </div>
                        <button onClick={() => window.close()} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium px-3 py-1 hover:bg-indigo-100 rounded-lg transition-colors">Close Tab</button>
                    </div>
                )}
                <MonthNavigation 
                    year={year}
                    month={month}
                    chartData={chartData}
                    animal={animal}
                    onExport={handleExportPDF}
                    exporting={exporting}
                    mode={mode}
                    onModeChange={setMode}
                    hasProAccess={hasProAccess}
                />

                {loading && <LoadingState />}
                {error && <ErrorState message={error} />}

                {chartData && !loading && (
                    <>
                        <ChartSection 
                            data={chartData.data} 
                            peakEnergy={peakEnergy} 
                            lowEnergy={lowEnergy} 
                            animationKey={`${animal}-${year}-${month}`}
                            mode={mode}
                            baziMonthlyEnergy={baziMonthlyEnergy}
                            loadingBazi={loadingBazi}
                            year={year}
                            month={month}
                            setMonth={setMonth}
                            isAdmin={isAdmin}
                        />
                        <StatsCards statistics={chartData.statistics} />
                        <DaysSection bestDays={chartData.statistics.best_days} worstDays={chartData.statistics.worst_days} />
                        <Legend />
                    </>
                )}
            </div>
        </div>
    );
}

function HeaderSection({ animal, animals, chartData, onBack, onAnimalChange }) {
    return (
        <div className="flex items-center justify-between mb-6">
            <button 
                onClick={onBack}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="back-button"
            >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
            </button>
            
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                {chartData?.animal?.chinese && (
                    <span className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold">
                        {chartData.animal.chinese}
                    </span>
                )}
                {chartData?.animal?.english || 'Energy Chart'}
            </h1>
            
            <select
                value={animal}
                onChange={onAnimalChange}
                className="px-4 py-2 border rounded-lg bg-white shadow-sm"
                data-testid="animal-selector"
            >
                {animals.map(a => (
                    <option key={a.id} value={a.id}>{a.chinese} {a.english}</option>
                ))}
            </select>
        </div>
    );
}

function MonthNavigation({ year, month, chartData, animal, onExport, exporting, mode, onModeChange, hasProAccess }) {
    const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false);
    
    const handleToggleClick = () => {
        if (hasProAccess) {
            onModeChange(mode === 'beginner' ? 'pro' : 'beginner');
            setShowUpgradePrompt(false);
        } else {
            // Show upgrade prompt when non-PRO user tries to toggle
            setShowUpgradePrompt(true);
        }
    };
    
    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
            <div className="flex items-center justify-between">
                {/* Mode Toggle */}
                <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2 relative group">
                        <span className={`text-sm ${mode === 'beginner' ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>Beginner</span>
                        <button
                            onClick={handleToggleClick}
                            className={`relative w-12 h-6 rounded-full transition-colors ${mode === 'pro' ? 'bg-amber-500' : 'bg-slate-300'}`}
                            data-testid="mode-toggle"
                        >
                            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${mode === 'pro' ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                        <span className={`text-sm ${mode === 'pro' ? 'font-semibold text-amber-600' : 'text-slate-400'}`}>PRO</span>
                    </div>
                    {/* Upgrade to PRO button - shown when non-PRO user tries to toggle */}
                    {!hasProAccess && showUpgradePrompt && (
                        <a 
                            href="/subscription" 
                            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-1 shadow-md"
                        >
                            Upgrade to PRO →
                        </a>
                    )}
                </div>
                
                {/* Title */}
                <div className="flex items-center gap-4">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <h2 className="text-xl font-semibold text-slate-800" data-testid="chart-title">
                        Energy Chart - {MONTH_NAMES[month - 1]} {year}
                    </h2>
                </div>
                
                {/* Export Button */}
                <div className="relative group">
                    <button
                        onClick={onExport}
                        disabled={exporting || !chartData || !hasProAccess}
                        className={`flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${!hasProAccess ? 'cursor-not-allowed' : ''}`}
                        data-testid="export-pdf-btn"
                    >
                        {exporting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileText className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">Export PDF</span>
                    </button>
                    {!hasProAccess && (
                        <a href="/subscription" className="absolute -bottom-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-amber-600 text-white text-xs px-3 py-1 rounded whitespace-nowrap z-50 hover:bg-amber-700">
                            Upgrade to PRO →
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading energy data...</p>
        </div>
    );
}

function ErrorState({ message }) {
    return (
        <div className="bg-red-50 rounded-2xl p-6 text-center text-red-600">
            {message}
        </div>
    );
}

function ChartSection({ data, peakEnergy, lowEnergy, animationKey, mode, baziMonthlyEnergy, loadingBazi, year, month, setMonth, isAdmin }) {
    const [animated, setAnimated] = useState(false);
    const isPro = mode === 'pro';
    
    useEffect(() => {
        setAnimated(false);
        const timer = setTimeout(() => setAnimated(true), 50);
        return () => clearTimeout(timer);
    }, [animationKey]);
    
    // Detect consecutive green bars (energy >= 80%)
    const getGreenStreaks = () => {
        const greenStreaks = [];
        let currentGreen = null;
        
        data.forEach((day) => {
            const isGreen = day.energy >= 80;
            
            if (isGreen) {
                if (!currentGreen) {
                    currentGreen = { start: day.day, end: day.day, days: [day] };
                } else {
                    currentGreen.end = day.day;
                    currentGreen.days.push(day);
                }
            } else {
                if (currentGreen && currentGreen.days.length >= 2) {
                    greenStreaks.push(currentGreen);
                }
                currentGreen = null;
            }
        });
        
        if (currentGreen && currentGreen.days.length >= 2) {
            greenStreaks.push(currentGreen);
        }
        
        return greenStreaks;
    };
    
    // Detect consecutive red bars (energy < 30%) - CAUTION zones
    const getRedStreaks = () => {
        const redStreaks = [];
        let currentRed = null;
        
        data.forEach((day) => {
            const isRed = day.energy < 30;
            
            if (isRed) {
                if (!currentRed) {
                    currentRed = { start: day.day, end: day.day, days: [day] };
                } else {
                    currentRed.end = day.day;
                    currentRed.days.push(day);
                }
            } else {
                if (currentRed && currentRed.days.length >= 2) {
                    redStreaks.push(currentRed);
                }
                currentRed = null;
            }
        });
        
        if (currentRed && currentRed.days.length >= 2) {
            redStreaks.push(currentRed);
        }
        
        return redStreaks;
    };
    
    // Calculate winning streak cycles - consecutive days without red bars (energy >= 30%)
    const getWinningStreaks = () => {
        const streaks = [];
        let currentStreak = null;
        
        data.forEach((day, index) => {
            const isWinning = day.energy >= 30; // Not a red bar
            
            if (isWinning) {
                if (!currentStreak) {
                    // Start new streak
                    currentStreak = { 
                        start: day.day, 
                        end: day.day, 
                        days: [day], 
                        energies: [day.energy],
                        qualities: [day.total_quality || 0]
                    };
                } else {
                    // Continue streak
                    currentStreak.end = day.day;
                    currentStreak.days.push(day);
                    currentStreak.energies.push(day.energy);
                    currentStreak.qualities.push(day.total_quality || 0);
                }
            } else {
                // Red bar - ends any current streak
                if (currentStreak && currentStreak.days.length >= 2) {
                    currentStreak.avgEnergy = currentStreak.energies.reduce((a, b) => a + b, 0) / currentStreak.energies.length;
                    currentStreak.maxEnergy = Math.max(...currentStreak.energies);
                    currentStreak.minEnergy = Math.min(...currentStreak.energies);
                    // Quality score based on Tong Shu factors
                    currentStreak.avgQuality = currentStreak.qualities.reduce((a, b) => a + b, 0) / currentStreak.qualities.length;
                    currentStreak.totalQuality = currentStreak.qualities.reduce((a, b) => a + b, 0);
                    
                    // Count factor highlights
                    currentStreak.yellowPathDays = currentStreak.days.filter(d => d.is_yellow_path).length;
                    currentStreak.bestRatingDays = currentStreak.days.filter(d => d.category === 'BEST').length;
                    currentStreak.goodOfficerDays = currentStreak.days.filter(d => 
                        ['Success', 'Danger', 'Open', 'Full'].includes(d.day_officer)
                    ).length;
                    
                    streaks.push(currentStreak);
                }
                currentStreak = null;
            }
        });
        
        // Don't forget the last streak
        if (currentStreak && currentStreak.days.length >= 2) {
            currentStreak.avgEnergy = currentStreak.energies.reduce((a, b) => a + b, 0) / currentStreak.energies.length;
            currentStreak.maxEnergy = Math.max(...currentStreak.energies);
            currentStreak.minEnergy = Math.min(...currentStreak.energies);
            currentStreak.avgQuality = currentStreak.qualities.reduce((a, b) => a + b, 0) / currentStreak.qualities.length;
            currentStreak.totalQuality = currentStreak.qualities.reduce((a, b) => a + b, 0);
            currentStreak.yellowPathDays = currentStreak.days.filter(d => d.is_yellow_path).length;
            currentStreak.bestRatingDays = currentStreak.days.filter(d => d.category === 'BEST').length;
            currentStreak.goodOfficerDays = currentStreak.days.filter(d => 
                ['Success', 'Danger', 'Open', 'Full'].includes(d.day_officer)
            ).length;
            streaks.push(currentStreak);
        }
        
        // Find the best streak based on QUALITY SCORE (not just energy)
        // Rank all streaks - top 3 get stars
        if (streaks.length > 0) {
            // Sort by avgQuality descending
            const sortedByQuality = [...streaks].sort((a, b) => b.avgQuality - a.avgQuality);
            
            streaks.forEach(s => {
                const rank = sortedByQuality.findIndex(sorted => sorted.start === s.start && sorted.end === s.end) + 1;
                s.rank = rank;
                s.isBest = rank === 1;
                s.isSecondBest = rank === 2;
                s.isThirdBest = rank === 3;
                s.isTopThree = rank <= 3;
            });
        }
        
        return streaks;
    };
    
    const streaks = getWinningStreaks();
    const greenStreaks = getGreenStreaks();
    const redStreaks = getRedStreaks();
    const bestStreak = streaks.find(s => s.isBest);
    
    // Calculate Monthly Energy Forecast
    const getMonthlyForecast = () => {
        const greenBars = data.filter(d => d.energy >= 80).length;
        const redBars = data.filter(d => d.energy < 30).length;
        const blueBars = data.filter(d => d.energy >= 30 && d.energy < 80).length;
        const avgEnergy = data.reduce((a, b) => a + b.energy, 0) / data.length;
        
        // Count all quality factors
        const yellowPathDays = data.filter(d => d.is_yellow_path).length;
        const blackPathDays = data.filter(d => d.is_yellow_path === false).length;
        const bestRatingDays = data.filter(d => d.category === 'BEST').length;
        const badRatingDays = data.filter(d => d.category === 'BAD').length;
        const averageRatingDays = data.filter(d => d.category === 'AVERAGE').length;
        
        // Day Officers
        const goodOfficerDays = data.filter(d => 
            ['Success', 'Danger', 'Open', 'Full'].includes(d.day_officer)
        ).length;
        const badOfficerDays = data.filter(d => 
            ['Destruction', 'Close'].includes(d.day_officer)
        ).length;
        
        // Day Breaker (日破) - days where animal is clashed
        const dayBreakerDays = data.filter(d => 
            d.factors && d.factors.some(f => f.factor && f.factor.includes('Day Breaker'))
        ).length;
        
        // Count auspicious and inauspicious stars
        let totalAuspiciousStars = 0;
        let totalInauspiciousStars = 0;
        data.forEach(d => {
            if (d.auspicious_stars) totalAuspiciousStars += d.auspicious_stars.length;
            if (d.inauspicious_stars) totalInauspiciousStars += d.inauspicious_stars.length;
        });
        
        // Count favorable constellations (simplified)
        const goodConstellationDays = data.filter(d => {
            const goodConstellations = ['Bow', 'Mound', 'Room', 'Star', 'House', 'Tail', 'Wall', 'Net', 'Stomach'];
            return d.constellation && goodConstellations.some(c => d.constellation.includes(c));
        }).length;
        const badConstellationDays = data.filter(d => {
            const badConstellations = ['Void', 'Pleaides', 'Ox', 'Beak', 'Ghost', 'Danger'];
            return d.constellation && badConstellations.some(c => d.constellation.includes(c));
        }).length;
        
        // Determine classification - Super Combo is 6 or more green bars
        let classification = '';
        let classColor = '';
        let icon = '';
        
        if (greenBars >= 6) {
            classification = 'Super Combo';
            classColor = 'from-emerald-500 to-green-600';
            icon = '🌟';
        } else if (redBars >= 8 && redBars <= 10) {
            classification = 'Promising (Caution)';
            classColor = 'from-amber-500 to-orange-500';
            icon = '⚠️';
        } else if (redBars >= 6 && redBars < 8 && greenBars < 4) {
            classification = 'Promising (Borderline)';
            classColor = 'from-orange-400 to-red-400';
            icon = '⚡';
        } else if (redBars > 10) {
            classification = 'Challenging';
            classColor = 'from-red-500 to-red-700';
            icon = '🔴';
        } else {
            classification = 'Promising';
            classColor = 'from-blue-500 to-indigo-500';
            icon = '✨';
        }
        
        // Generate comprehensive advice based on ALL factors
        let expectation = '';
        let decisionAdvice = '';
        let energyDescription = '';
        
        // Calculate overall positive/negative factor ratio
        const positiveScore = yellowPathDays + bestRatingDays + goodOfficerDays + goodConstellationDays + (totalAuspiciousStars / 3);
        const negativeScore = blackPathDays + badRatingDays + badOfficerDays + dayBreakerDays + badConstellationDays + (totalInauspiciousStars / 3);
        
        if (greenBars >= 6) {
            expectation = `This month radiates exceptional cosmic energy for your personal chart. With ${greenBars} peak energy days reaching 80% or higher, the celestial alignments strongly favor bold initiatives, important life decisions, and new ventures. The presence of ${yellowPathDays} Yellow Path (黃道) days indicates divine timing support, while ${goodOfficerDays} days blessed by favorable Day Officers (Success, Danger, Open, Full) amplify your chances of achievement. The ${goodConstellationDays} days with auspicious 28 Constellations (二十八宿) create windows of heightened opportunity. This is truly a Super Combo month where the stars align in your favor.`;
            
            decisionAdvice = `Maximize this exceptional period by scheduling major decisions, contract signings, business launches, and important negotiations during your green-bar days. The ${bestRatingDays} days with BEST personal chart compatibility are ideal for personal matters and relationships. With ${totalAuspiciousStars} auspicious star (吉星) influences throughout the month, your endeavors receive celestial blessings. However, remain mindful of the ${dayBreakerDays} Day Breaker (日破) days when your animal sign faces direct clash – avoid major commitments on these dates. Use the ${blackPathDays} Black Path (黑道) days for rest, planning, and low-key activities rather than important launches.`;
            
            energyDescription = `Your monthly energy profile shows a powerful distribution: ${greenBars} high-energy days, ${blueBars} stable days, and only ${redBars} challenging days. The ${totalAuspiciousStars} auspicious stars versus ${totalInauspiciousStars} inauspicious stars ratio favors positive outcomes. Among the 12 Day Officers (建除十二神), you benefit from ${goodOfficerDays} favorable placements. The 28 Lunar Mansions contribute ${goodConstellationDays} auspicious alignments. This comprehensive cosmic support makes this an outstanding month for progress and achievement.`;
        } else if (redBars >= 8) {
            expectation = `This month requires heightened awareness and strategic navigation. With ${redBars} low-energy days falling below 30%, combined with ${dayBreakerDays} Day Breaker (日破) clashes against your personal chart, the cosmic currents present notable challenges. The presence of ${badOfficerDays} unfavorable Day Officers (Destruction, Close) and ${badConstellationDays} inauspicious constellation days demands careful timing. The ${totalInauspiciousStars} inauspicious star (凶星) influences suggest exercising patience and avoiding impulsive decisions. However, with ${greenBars} green-bar days still available, strategic windows for action exist.`;
            
            decisionAdvice = `Navigate this period by concentrating important activities on your ${greenBars} peak energy days and ${bestRatingDays} BEST-rated days. Absolutely avoid major decisions, investments, or confrontations during Day Breaker (日破) periods when your sign faces direct cosmic clash. The ${blackPathDays} Black Path (黑道) days are best reserved for introspection, planning, and completing existing projects rather than initiating new ones. Leverage the ${yellowPathDays} Yellow Path (黃道) days for time-sensitive matters. Pay attention to the ${goodOfficerDays} favorable Day Officer placements as safe havens for moderate activity.`;
            
            energyDescription = `Analysis reveals ${redBars} challenging days requiring caution, balanced against ${greenBars} high-opportunity days and ${blueBars} neutral days. The ${totalInauspiciousStars} inauspicious stars (凶星) outweigh the ${totalAuspiciousStars} auspicious influences, indicating a need for defensive positioning. Among the 28 Constellations, ${badConstellationDays} days carry negative mansion influences. Focus on damage control during difficult periods and capitalize fully on the available positive windows.`;
        } else if (redBars >= 6 && greenBars < 4) {
            expectation = `This month presents a mixed energy landscape requiring balanced navigation. With ${redBars} low-energy days and only ${greenBars} peak days, careful selection of timing becomes essential. The ${dayBreakerDays} Day Breaker (日破) occurrences add complexity to your planning. While ${badOfficerDays} days carry unfavorable Day Officer influences and ${badConstellationDays} challenging constellation alignments, the month is not without opportunity. Your ${yellowPathDays} Yellow Path days and ${bestRatingDays} BEST-rated periods offer windows for progress.`;
            
            decisionAdvice = `Adopt a selective, quality-over-quantity approach this month. Reserve your most important decisions for the ${greenBars} green-bar days when energy peaks above 80%. The ${goodOfficerDays} favorable Day Officer days (Success, Danger, Open, Full) provide secondary timing options for moderate initiatives. Strictly avoid the Day Breaker (日破) dates and ${badOfficerDays} Destruction/Close officer days for any significant commitments. Use the ${blackPathDays} Black Path periods for reflection, research, and preparation. The ${totalAuspiciousStars} auspicious stars offer some support, but don't overextend during this borderline period.`;
            
            energyDescription = `The energy distribution shows ${greenBars} opportunity days, ${blueBars} stable days, and ${redBars} caution days. With ${badRatingDays} days carrying BAD personal chart compatibility ratings and ${averageRatingDays} AVERAGE days, personal energy requires careful management. The constellation map reveals ${goodConstellationDays} favorable and ${badConstellationDays} unfavorable mansion placements. Monitor the interplay between ${totalAuspiciousStars} auspicious and ${totalInauspiciousStars} inauspicious stellar influences for optimal timing.`;
        } else {
            expectation = `This month offers promising energy potential with solid foundations for progress. Your ${greenBars} high-energy days provide excellent timing for important matters, supported by ${yellowPathDays} Yellow Path (黃道) auspicious days. The ${bestRatingDays} days with BEST personal chart compatibility enhance personal endeavors, while ${goodOfficerDays} favorable Day Officer placements (Success, Danger, Open, Full) create productive windows. With ${goodConstellationDays} favorable constellation days from the 28 Lunar Mansions (二十八宿), the month supports steady advancement.`;
            
            decisionAdvice = `Build momentum by aligning important activities with your strongest energy days. Prioritize the ${greenBars} peak days for major decisions, contracts, and launches. The ${bestRatingDays} BEST compatibility days favor personal relationships and individual goals. Utilize the ${yellowPathDays} Yellow Path (黃道) days for matters requiring luck and divine timing. Maintain awareness of the ${dayBreakerDays} Day Breaker (日破) days when your sign faces clash – schedule rest or routine tasks during these periods. The ${totalAuspiciousStars} auspicious star blessings throughout the month support positive outcomes when properly timed.`;
            
            energyDescription = `Your energy profile reveals a constructive distribution: ${greenBars} excellent days, ${blueBars} stable days, and ${redBars} challenging days requiring caution. The 12 Day Officers (建除十二神) provide ${goodOfficerDays} favorable placements against ${badOfficerDays} unfavorable ones. Among the 28 Constellations, ${goodConstellationDays} days carry beneficial mansion energy. The stellar balance shows ${totalAuspiciousStars} auspicious influences (吉星) supporting your endeavors. This Promising month rewards consistent effort and wise timing.`;
        }
        
        return {
            classification,
            classColor,
            icon,
            greenBars,
            redBars,
            blueBars,
            avgEnergy,
            yellowPathDays,
            blackPathDays,
            bestRatingDays,
            badRatingDays,
            goodOfficerDays,
            badOfficerDays,
            dayBreakerDays,
            goodConstellationDays,
            badConstellationDays,
            totalAuspiciousStars,
            totalInauspiciousStars,
            expectation,
            decisionAdvice,
            energyDescription
        };
    };
    
    const forecast = getMonthlyForecast();
    
    // Color based on streak rank and length
    const getStreakColor = (streak) => {
        if (streak.isBest) return { bg: 'rgba(234, 179, 8, 0.25)', border: 'rgba(234, 179, 8, 0.8)' }; // gold for #1 (3 stars)
        if (streak.isSecondBest) return { bg: 'rgba(234, 179, 8, 0.18)', border: 'rgba(234, 179, 8, 0.6)' }; // lighter gold for #2 (2 stars)
        if (streak.isThirdBest) return { bg: 'rgba(234, 179, 8, 0.12)', border: 'rgba(234, 179, 8, 0.4)' }; // lightest gold for #3 (1 star)
        const length = streak.days.length;
        if (length >= 7) return { bg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 0.6)' }; // emerald
        if (length >= 5) return { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.6)' }; // blue
        if (length >= 3) return { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.6)' }; // purple
        return { bg: 'rgba(156, 163, 175, 0.2)', border: 'rgba(156, 163, 175, 0.6)' }; // gray for 2-day
    };
    
    // Get star display for ranked streaks
    const getStreakStars = (streak) => {
        if (streak.isBest) return '⭐⭐⭐';
        if (streak.isSecondBest) return '⭐⭐';
        if (streak.isThirdBest) return '⭐';
        return '';
    };
    
    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-6" data-testid="energy-chart">
            {/* Winning Streaks Count - PRO only */}
            {isPro && (
            <div className="flex justify-between items-center mb-2">
                <div className="text-xs text-slate-500">
                    {streaks.length > 0 && (
                        <span>{streaks.length} winning streaks found</span>
                    )}
                </div>
            </div>
            )}
            
            {/* Yearly Energy Overview - PRO only (Same as Crimson Horse) */}
            {isPro && baziMonthlyEnergy.length > 0 && (
                <div className="mb-4 bg-white rounded-xl border shadow-sm overflow-hidden">
                    <div className="p-4 border-b">
                        <h3 className="text-lg font-bold text-slate-800">
                            {year} Monthly Energy Outlook
                        </h3>
                        <p className="text-sm text-slate-500">Based on your BaZi interaction with monthly branches</p>
                    </div>
                    <div className="p-4">
                        {/* Legend */}
                        <div className="flex items-center justify-center gap-6 mb-4 text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-emerald-400 rounded" />
                                <span className="text-slate-600">Positive Energy</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-red-400 rounded" />
                                <span className="text-slate-600">Challenges</span>
                            </div>
                        </div>
                        {/* Monthly Bars - Exact Crimson Horse style */}
                        <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                            {MONTH_NAMES.map((monthName, i) => {
                                const energyData = baziMonthlyEnergy[i] || { positive: 50, challenge: 15 };
                                const positive = energyData.positive;
                                const challenge = energyData.challenge;
                                const total = positive + challenge;
                                const positiveHeight = (positive / total) * 100;
                                const challengeHeight = (challenge / total) * 100;
                                const isSelectedMonth = (i + 1) === month;
                                const currentMonth = new Date().getMonth() + 1;
                                const isActualCurrentMonth = (i + 1) === currentMonth;
                                const isFutureMonth = (i + 1) > currentMonth;
                                const isPastMonth = (i + 1) < currentMonth;
                                // Admin can click all months, PRO users can click current + past months (and always the actual current month)
                                const canClick = isAdmin || isActualCurrentMonth || isPastMonth;
                                
                                return (
                                    <div 
                                        key={i} 
                                        className={`text-center transition-transform ${canClick ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-60'} ${isSelectedMonth ? 'ring-2 ring-purple-500 ring-offset-2 rounded-lg' : ''} ${isActualCurrentMonth && !isSelectedMonth ? 'ring-2 ring-amber-400 ring-offset-1 rounded-lg' : ''}`}
                                        onClick={() => canClick && setMonth(i + 1)}
                                    >
                                        <div className={`text-xs font-medium mb-1 ${isActualCurrentMonth ? 'text-amber-600 font-bold' : 'text-slate-600'}`}>
                                            {monthName.slice(0, 3)}
                                            {isActualCurrentMonth && <span className="ml-1">•</span>}
                                        </div>
                                        <div className={`h-24 bg-slate-100 rounded-lg relative overflow-hidden flex flex-col justify-end ${isFutureMonth && !isAdmin ? 'grayscale' : ''}`}>
                                            <div 
                                                className="w-full bg-red-400 transition-all duration-700"
                                                style={{ height: `${challengeHeight}%` }}
                                            />
                                            <div 
                                                className="w-full bg-emerald-400 transition-all duration-700"
                                                style={{ height: `${positiveHeight}%` }}
                                            />
                                            {/* Lock icon for future months */}
                                            {isFutureMonth && !isAdmin && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-slate-200/70 cursor-pointer" onClick={(e) => { e.stopPropagation(); window.location.href = '/subscription'; }}>
                                                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex justify-center gap-1 mt-1">
                                            <span className="text-xs font-semibold text-emerald-600">{positive}</span>
                                            <span className="text-xs text-slate-400">/</span>
                                            <span className="text-xs font-semibold text-red-500">{challenge}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Upgrade to PRO prompt for non-PRO users viewing future months */}
            {!isPro && (
                <div className="mb-4 p-4 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold text-amber-800">Unlock Future Months</h3>
                                <p className="text-sm text-amber-600">Upgrade to PRO to view all months and advanced features</p>
                            </div>
                        </div>
                        <a 
                            href="/subscription" 
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                        >
                            <span>Upgrade to PRO</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded">$7 Trial</span>
                        </a>
                    </div>
                </div>
            )}
            
            {/* Loading state for BaZi data */}
            {isPro && loadingBazi && baziMonthlyEnergy.length === 0 && (
                <div className="mb-4 bg-white rounded-xl border shadow-sm p-4 flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                    <span className="text-sm text-slate-600">Loading monthly energy outlook...</span>
                </div>
            )}
            
            {/* Monthly Energy Forecast - Always visible */}
            <div className={`mb-4 p-4 rounded-xl border bg-gradient-to-r ${forecast.classColor} bg-opacity-10`}>
                <div className="bg-white/95 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-2xl">{forecast.icon}</span>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">Monthly Energy Forecast</h3>
                                <span className={`text-sm font-semibold bg-gradient-to-r ${forecast.classColor} bg-clip-text text-transparent`}>
                                    {forecast.classification}
                                </span>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-slate-800">{forecast.avgEnergy.toFixed(0)}%</div>
                            <div className="text-xs text-slate-500">Avg Energy</div>
                        </div>
                    </div>
                    
                    {/* Energy Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="text-center p-2 bg-emerald-50 rounded-lg">
                            <div className="text-lg font-bold text-emerald-600">{forecast.greenBars}</div>
                            <div className="text-[10px] text-emerald-700">Green Days</div>
                        </div>
                        <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <div className="text-lg font-bold text-blue-600">{forecast.blueBars}</div>
                            <div className="text-[10px] text-blue-700">Blue Days</div>
                        </div>
                        <div className="text-center p-2 bg-red-50 rounded-lg">
                            <div className="text-lg font-bold text-red-600">{forecast.redBars}</div>
                            <div className="text-[10px] text-red-700">Red Days</div>
                        </div>
                    </div>
                    
                    {/* PRO Mode: Detailed Forecast Description */}
                    {isPro && (
                        <>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <span className="font-semibold text-slate-700">Expectation: </span>
                                    <span className="text-slate-600">{forecast.expectation}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-slate-700">Decision Making: </span>
                                    <span className="text-slate-600">{forecast.decisionAdvice}</span>
                                </div>
                                <div className="pt-2 border-t">
                                    <span className="font-semibold text-slate-700">Energy Analysis: </span>
                                    <span className="text-slate-600">{forecast.energyDescription}</span>
                                </div>
                            </div>
                            
                            {/* Factor Summary */}
                            <div className="mt-4 pt-3 border-t">
                                <div className="text-xs font-semibold text-slate-500 mb-2">CALCULATION FACTORS</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                                    <div className="flex justify-between p-1.5 bg-yellow-50 rounded">
                                        <span className="text-slate-600">黃道 Yellow Path</span>
                                        <span className="font-bold text-yellow-600">{forecast.yellowPathDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-slate-100 rounded">
                                        <span className="text-slate-600">黑道 Black Path</span>
                                        <span className="font-bold text-slate-600">{forecast.blackPathDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-emerald-50 rounded">
                                        <span className="text-slate-600">BEST Rating</span>
                                        <span className="font-bold text-emerald-600">{forecast.bestRatingDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-blue-50 rounded">
                                        <span className="text-slate-600">Good Officers</span>
                                        <span className="font-bold text-blue-600">{forecast.goodOfficerDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-red-50 rounded">
                                        <span className="text-slate-600">日破 Day Breaker</span>
                                        <span className="font-bold text-red-600">{forecast.dayBreakerDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-purple-50 rounded">
                                        <span className="text-slate-600">二十八宿 Good</span>
                                        <span className="font-bold text-purple-600">{forecast.goodConstellationDays}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-green-50 rounded">
                                        <span className="text-slate-600">吉星 Auspicious</span>
                                        <span className="font-bold text-green-600">{forecast.totalAuspiciousStars}</span>
                                    </div>
                                    <div className="flex justify-between p-1.5 bg-orange-50 rounded">
                                        <span className="text-slate-600">凶星 Inauspicious</span>
                                        <span className="font-bold text-orange-600">{forecast.totalInauspiciousStars}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    
                    {/* Beginner Mode: Simple Summary */}
                    {!isPro && (
                        <div className="text-sm text-slate-600">
                            <p>This month has <strong className="text-emerald-600">{forecast.greenBars} excellent days</strong> for important activities. 
                            {forecast.redBars > 0 && <span> Be cautious on <strong className="text-red-600">{forecast.redBars} challenging days</strong>.</span>}
                            {forecast.dayBreakerDays > 0 && <span> Avoid major decisions on <strong className="text-red-600">{forecast.dayBreakerDays} Day Breaker</strong> days.</span>}
                            </p>
                            <p className="mt-2 text-xs text-slate-400">Switch to PRO mode for detailed analysis.</p>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Top Streaks Highlight - PRO only */}
            {isPro && streaks.length > 0 && (
                <div className="mb-3 space-y-2">
                    {streaks.filter(s => s.isTopThree).sort((a, b) => a.rank - b.rank).map((streak) => {
                        const stars = streak.isBest ? '⭐⭐⭐' : streak.isSecondBest ? '⭐⭐' : '⭐';
                        const label = streak.isBest ? 'Best' : streak.isSecondBest ? '2nd Best' : '3rd Best';
                        const bgClass = streak.isBest 
                            ? 'from-yellow-50 to-amber-50 border-yellow-300' 
                            : streak.isSecondBest 
                                ? 'from-yellow-50/70 to-amber-50/70 border-yellow-200'
                                : 'from-yellow-50/50 to-amber-50/50 border-yellow-100';
                        const textClass = streak.isBest 
                            ? 'text-yellow-800' 
                            : streak.isSecondBest 
                                ? 'text-yellow-700'
                                : 'text-yellow-600';
                        const subTextClass = streak.isBest 
                            ? 'text-yellow-600' 
                            : streak.isSecondBest 
                                ? 'text-yellow-500'
                                : 'text-yellow-400';
                        
                        return (
                            <div key={streak.start} className={`p-3 bg-gradient-to-r ${bgClass} border rounded-lg`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-base">{stars}</span>
                                    <div>
                                        <span className={`text-sm font-semibold ${textClass}`}>
                                            {label} Streak: Days {streak.start}-{streak.end}
                                        </span>
                                        <span className={`ml-2 text-xs ${subTextClass}`}>
                                            ({streak.days.length} days, avg {streak.avgEnergy.toFixed(0)}%)
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 ml-7 text-[10px]">
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                        {streak.bestRatingDays} BEST days
                                    </span>
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                        {streak.yellowPathDays} 黃道 days
                                    </span>
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        {streak.goodOfficerDays} good officers
                                    </span>
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                        Quality: {streak.avgQuality.toFixed(0)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {/* Chart with cycle overlays */}
            <div className="relative">
                {/* Winning streak overlays - PRO only */}
                {isPro && (
                <div className="absolute inset-0 flex pointer-events-none" style={{ bottom: '24px', top: '24px' }}>
                        {streaks.map((streak, idx) => {
                            const startPercent = ((streak.start - 1) / data.length) * 100;
                            const widthPercent = ((streak.end - streak.start + 1) / data.length) * 100;
                            const colors = getStreakColor(streak);
                            const starIcon = getStreakStars(streak);
                            return (
                                <div
                                    key={idx}
                                    className="absolute top-0 bottom-0 rounded-2xl"
                                    style={{
                                        left: `${startPercent}%`,
                                        width: `${widthPercent}%`,
                                        backgroundColor: colors.bg,
                                        border: `3px dashed ${colors.border}`,
                                        boxShadow: streak.isTopThree ? `0 0 12px ${colors.border}` : 'none',
                                    }}
                                >
                                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex flex-col items-center">
                                        {starIcon && (
                                            <span className="text-sm mb-0.5">{starIcon}</span>
                                        )}
                                        <span 
                                            className="text-[10px] font-bold whitespace-nowrap px-1.5 py-0.5 rounded"
                                            style={{ 
                                                backgroundColor: colors.border,
                                                color: 'white'
                                            }}
                                        >
                                            {streak.days.length} DAYS
                                        </span>
                                    </div>
                                    {/* Show average energy and quality at bottom */}
                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-[9px]">
                                        <span style={{ color: colors.border }} className="font-medium">
                                            avg {streak.avgEnergy.toFixed(0)}%
                                        </span>
                                        <span className="text-slate-400">
                                            Q:{streak.avgQuality.toFixed(0)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Green streak highlights (consecutive 80%+ days) - PRO only */}
                {isPro && greenStreaks.length > 0 && (
                    <div className="absolute inset-0 flex pointer-events-none" style={{ bottom: '24px', top: '24px' }}>
                        {greenStreaks.map((gs, idx) => {
                            const startPercent = ((gs.start - 1) / data.length) * 100;
                            const widthPercent = ((gs.end - gs.start + 1) / data.length) * 100;
                            const isTriple = gs.days.length >= 3;
                            return (
                                <div
                                    key={`green-${idx}`}
                                    className="absolute rounded-2xl"
                                    style={{
                                        left: `${startPercent}%`,
                                        width: `${widthPercent}%`,
                                        top: '10%',
                                        bottom: '15%',
                                        background: 'rgba(16, 185, 129, 0.15)',
                                        border: '3px dashed rgba(16, 185, 129, 0.8)',
                                    }}
                                >
                                    <div 
                                        className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap"
                                        style={{
                                            background: isTriple 
                                                ? 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' 
                                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        {isTriple ? '★★★ HOT STREAK' : '★★ DOUBLE GREEN'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Red streak highlights (consecutive <30% days) - CAUTION - PRO only */}
                {isPro && redStreaks.length > 0 && (
                    <div className="absolute inset-0 flex pointer-events-none" style={{ bottom: '24px', top: '24px' }}>
                        {redStreaks.map((rs, idx) => {
                            const startPercent = ((rs.start - 1) / data.length) * 100;
                            const widthPercent = ((rs.end - rs.start + 1) / data.length) * 100;
                            const isTriple = rs.days.length >= 3;
                            return (
                                <div
                                    key={`red-${idx}`}
                                    className="absolute rounded-2xl"
                                    style={{
                                        left: `${startPercent}%`,
                                        width: `${widthPercent}%`,
                                        top: '45%',
                                        bottom: '0',
                                        background: 'rgba(239, 68, 68, 0.12)',
                                        border: '3px dashed rgba(239, 68, 68, 0.7)',
                                    }}
                                >
                                    <div 
                                        className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                                        style={{
                                            background: isTriple 
                                                ? 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)' 
                                                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            color: 'white',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        {isTriple ? '⚠ DANGER' : '⚠ CAUTION'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Bars */}
                <div className="flex items-end gap-[2px] pb-8 relative z-10 pt-8">
                    {data.map((day, index) => (
                        <EnergyBar 
                            key={day.day} 
                            day={day} 
                            peakEnergy={peakEnergy} 
                            lowEnergy={lowEnergy}
                            index={index}
                            animated={animated}
                        />
                    ))}
                </div>
            </div>
            <div className="text-center text-slate-500 mt-2 text-sm">Day</div>
            
            {/* Streak Legend */}
            <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-slate-600 mb-2 text-center">
                    <strong>Winning Streaks:</strong> Consecutive days with energy ≥30% (no red bars)
                </div>
                <div className="flex flex-wrap gap-3 justify-center text-xs">
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-bold" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'}}>★★★</span>
                        <span className="text-slate-600">Hot Streak</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-bold" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>★★</span>
                        <span className="text-slate-600">Double Green</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-bold" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>⚠</span>
                        <span className="text-slate-600">Caution</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-bold" style={{background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)'}}>⚠️</span>
                        <span className="text-slate-600">Danger Zone</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-yellow-500">⭐</span>
                        <span className="text-slate-600">Best Streak</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatsCards({ statistics }) {
    return (
        <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-5" data-testid="avg-energy-card">
                <div className="text-slate-500 text-sm mb-1">Average Energy</div>
                <div className="text-3xl font-bold text-slate-800">{statistics.average}%</div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl shadow-lg p-5" data-testid="peak-day-card">
                <div className="flex items-center gap-2 text-emerald-600 text-sm mb-1">
                    <TrendingUp className="w-4 h-4" />
                    Peak Day
                </div>
                <div className="text-3xl font-bold text-emerald-700">Day {statistics.peak_day.day}</div>
                <div className="text-emerald-600 text-sm">{statistics.peak_day.energy}% energy</div>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-lg p-5" data-testid="low-day-card">
                <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                    <TrendingDown className="w-4 h-4" />
                    Lowest Day
                </div>
                <div className="text-3xl font-bold text-red-700">Day {statistics.low_day.day}</div>
                <div className="text-red-600 text-sm">{statistics.low_day.energy}% energy</div>
            </div>
        </div>
    );
}

function DaysSection({ bestDays, worstDays }) {
    return (
        <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="best-days-card">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                    Best Days This Month
                </h3>
                <div className="space-y-3">
                    {bestDays.map((day, idx) => (
                        <DayListItem key={day.day} day={day} rank={idx + 1} variant="best" />
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="worst-days-card">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    Days to Be Cautious
                </h3>
                <div className="space-y-3">
                    {worstDays.map((day, idx) => (
                        <DayListItem key={day.day} day={day} rank={idx + 1} variant="worst" />
                    ))}
                </div>
            </div>
        </div>
    );
}

function Legend() {
    const barItems = [
        { color: 'bg-emerald-500', label: 'High Energy (80%+)' },
        { color: 'bg-blue-500', label: 'Average Energy (30-79%)' },
        { color: 'bg-red-500', label: 'Low Energy (<30%)' },
    ];
    
    return (
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Legend</h3>
            <div className="flex flex-wrap gap-6 mb-4">
                {barItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                        <div className={`w-6 h-4 ${item.color} rounded`}></div>
                        <span className="text-sm text-slate-600">{item.label}</span>
                    </div>
                ))}
            </div>
            <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Calculation Factors:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-slate-600">
                    <div>• <b>Animal Rating</b> - Personal chart compatibility</div>
                    <div>• <b>12 Day Officers</b> - 建除十二神 daily energy</div>
                    <div>• <b>Day Breaker</b> - 日破 clash penalty</div>
                    <div>• <b>28 Constellations</b> - 二十八宿 lunar mansions</div>
                    <div>• <b>黃道 Yellow Path</b> - Auspicious belt (+bonus)</div>
                    <div>• <b>黑道 Black Path</b> - Inauspicious belt (-penalty)</div>
                    <div>• <b>Auspicious Stars</b> - 吉星 good daily stars</div>
                    <div>• <b>Inauspicious Stars</b> - 凶星 negative daily stars</div>
                </div>
            </div>
        </div>
    );
}
